import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import JobCard from "../components/JobCard";
import JobSearch from "../components/JobSearch";
import { Button } from "../components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { X, Mail } from "lucide-react";
import { useSeo } from "../lib/useSeo";

const ROLES = [
  { v: "all", l: "All roles" },
  { v: "train-driver", l: "Train Driver" },
  { v: "conductor", l: "Conductor" },
  { v: "depot-driver", l: "Depot Driver / Shunter" },
  { v: "fitter", l: "Fitter / Maintenance" },
  { v: "engineering", l: "Engineering" },
  { v: "signalling", l: "Signalling" },
  { v: "customer-service", l: "Customer Service" },
  { v: "graduate", l: "Graduate" },
  { v: "apprentice", l: "Apprenticeship" },
  { v: "other", l: "Other" },
];

const LISTING_COUNTS = [
  { v: "10", l: "10 jobs" },
  { v: "20", l: "20 jobs" },
  { v: "50", l: "50 jobs" },
  { v: "all", l: "All jobs" },
];

function CompactNewsletterSignup() {
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState("idle");
  const [message, setMessage] = useState("");

  const submitNewsletter = async (e) => {
    e.preventDefault();

    const cleanEmail = email.trim();

    if (!cleanEmail) {
      setStatus("error");
      setMessage("Enter your email address.");
      return;
    }

    setStatus("loading");
    setMessage("");

    try {
      await api.post("/newsletter/subscribe", { email: cleanEmail });

      setStatus("success");
      setMessage("You're signed up.");
      setEmail("");
    } catch (error) {
      console.error("Newsletter signup failed", error);

      setStatus("error");
      setMessage("Signup failed. Please try again.");
    }
  };

  return (
    <div className="mt-5 border-t border-line pt-5" data-testid="vac-newsletter">
      <div className="flex items-start gap-2">
        <div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-sky/10 text-sky">
          <Mail className="h-4 w-4" />
        </div>

        <div className="min-w-0">
          <div className="font-display text-ice text-[15px] font-bold leading-tight">
            Job alerts
          </div>

          <p className="mt-1 text-slate text-[12px] leading-relaxed">
            Get new UK rail vacancies sent to your inbox.
          </p>
        </div>
      </div>

      <form onSubmit={submitNewsletter} className="mt-4 space-y-2">
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="Email address"
          className="w-full min-w-0 rounded-lg border border-line bg-nav px-3 py-2 text-[13px] text-ice placeholder:text-slate outline-none focus:border-sky"
          data-testid="vac-newsletter-email"
        />

        <Button
          type="submit"
          disabled={status === "loading"}
          className="w-full text-[13px]"
          data-testid="vac-newsletter-submit"
        >
          {status === "loading" ? "Signing up..." : "Sign up"}
        </Button>
      </form>

      {message && (
        <div
          className={`mt-2 text-[12px] leading-relaxed ${
            status === "success" ? "text-sky" : "text-red-300"
          }`}
        >
          {message}
        </div>
      )}
    </div>
  );
}

export default function Vacancies() {
  const [params, setParams] = useSearchParams();

  const [vacancies, setVacancies] = useState([]);
  const [role, setRole] = useState(params.get("role") || "all");
  const [region, setRegion] = useState(params.get("region") || "all");
  const [q, setQ] = useState(params.get("q") || "");
  const [employer, setEmployer] = useState(params.get("employer") || "");
  const [listingCount, setListingCount] = useState(params.get("show") || "10");
  const [loading, setLoading] = useState(true);

  const filterDesc = [
    employer && `at ${employer}`,
    role !== "all" && `(${role})`,
    region !== "all" && `in ${region}`,
  ]
    .filter(Boolean)
    .join(" ");

  useSeo({
    title: `Live UK rail vacancies${
      filterDesc ? " " + filterDesc : ""
    } | Railway Careers UK`,
    description: `Browse every live UK rail vacancy${
      filterDesc ? " " + filterDesc : ""
    }. Train Operating Companies, Network Rail, HS2, freight operators and Tier-1 contractors. Apply directly with each employer — no recruiters, no fees.`,
    canonical: "/vacancies",
  });

  const fetchVacancies = async () => {
    setLoading(true);

    const search = {};

    if (role !== "all") search.role = role;
    if (region !== "all") search.region = region;
    if (q.trim()) search.q = q.trim();
    if (employer) search.employer = employer;

    try {
      const r = await api.get("/vacancies", { params: search });
      setVacancies(Array.isArray(r.data) ? r.data : []);
    } catch (error) {
      console.error("Failed to fetch vacancies", error);
      setVacancies([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchVacancies();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [role, region, q, employer]);

  useEffect(() => {
    setRole(params.get("role") || "all");
    setRegion(params.get("region") || "all");
    setQ(params.get("q") || "");
    setEmployer(params.get("employer") || "");
    setListingCount(params.get("show") || "10");
  }, [params]);

  const updateListingCount = (value) => {
    setListingCount(value);

    const p = new URLSearchParams(params);

    if (value === "10") {
      p.delete("show");
    } else {
      p.set("show", value);
    }

    setParams(p);
  };

  const regions = useMemo(() => {
    const s = new Set(vacancies.map((v) => v.region).filter(Boolean));
    return ["all", ...Array.from(s)];
  }, [vacancies]);

  const featured = useMemo(
    () => vacancies.filter((v) => v.featured),
    [vacancies]
  );

  const standard = useMemo(
    () => vacancies.filter((v) => !v.featured),
    [vacancies]
  );

  const displayedStandard = useMemo(() => {
    if (listingCount === "all") return standard;
    return standard.slice(0, Number(listingCount));
  }, [standard, listingCount]);

  const hiddenStandardCount = Math.max(
    standard.length - displayedStandard.length,
    0
  );

  const displayedTotal = featured.length + displayedStandard.length;

  const reset = () => {
    setRole("all");
    setRegion("all");
    setQ("");
    setEmployer("");
    setListingCount("10");
    setParams({});
  };

  const clearEmployer = () => {
    setEmployer("");

    const p = new URLSearchParams(params);
    p.delete("employer");
    setParams(p);
  };

  return (
    <div className="bg-nav min-h-screen">
      <div className="max-w-7xl mx-auto px-6 md:px-12 pt-12 pb-8">
        <div className="chapter-mark">Live Vacancies</div>

        <h1 className="mt-6 font-display text-ice text-4xl md:text-6xl tracking-[-0.025em] font-extrabold leading-[1.05]">
          Search every live <span className="text-sky">UK rail vacancy.</span>
        </h1>

        <p className="mt-4 text-slate text-lg max-w-2xl">
          Posted by Railway Careers UK. Featured roles pinned to the top — every
          vacancy deep-links to the operator&apos;s original application page to
          apply.
        </p>

        <div className="mt-8">
          <JobSearch />
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 md:px-12 pb-20 mt-6 grid lg:grid-cols-[260px_1fr] gap-8">
        <aside className="space-y-5 lg:sticky lg:top-24 self-start">
          <div className="paper-card p-5">
            <div className="text-[11px] uppercase tracking-[0.16em] text-sky font-semibold mb-3">
              Role
            </div>

            <Select value={role} onValueChange={setRole}>
              <SelectTrigger
                className="bg-nav border-line"
                data-testid="vac-role-select"
              >
                <SelectValue />
              </SelectTrigger>

              <SelectContent>
                {ROLES.map((o) => (
                  <SelectItem key={o.v} value={o.v}>
                    {o.l}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="paper-card p-5">
            <div className="text-[11px] uppercase tracking-[0.16em] text-sky font-semibold mb-3">
              Region
            </div>

            <Select value={region} onValueChange={setRegion}>
              <SelectTrigger
                className="bg-nav border-line"
                data-testid="vac-region-select"
              >
                <SelectValue />
              </SelectTrigger>

              <SelectContent>
                {regions.map((r) => (
                  <SelectItem key={r} value={r}>
                    {r === "all" ? "All UK regions" : r}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="paper-card p-5 overflow-hidden">
            <div className="text-[11px] uppercase tracking-[0.16em] text-sky font-semibold">
              Results
            </div>

            <div className="font-display text-ice text-3xl mt-2 font-bold">
              {vacancies.length}
            </div>

            <div className="text-slate text-[12px] mt-1">
              {vacancies.length === 1 ? "vacancy" : "vacancies"} matching
            </div>

            {(role !== "all" || region !== "all" || q || employer) && (
              <Button
                variant="outline"
                onClick={reset}
                className="mt-4 w-full border-line text-ice hover:bg-raise"
                data-testid="vac-clear-filters"
              >
                <X className="w-3.5 h-3.5 mr-1.5" />
                Clear filters
              </Button>
            )}

            <CompactNewsletterSignup />
          </div>

          {employer && (
            <div className="paper-card p-5 border-sky/40">
              <div className="text-[11px] uppercase tracking-[0.16em] text-sky font-semibold mb-2">
                Filtered by operator
              </div>

              <div className="font-display text-ice font-bold text-[15px]">
                {employer}
              </div>

              <button
                onClick={clearEmployer}
                className="mt-3 text-[12px] text-sky hover:text-ice inline-flex items-center gap-1.5"
                data-testid="vac-clear-employer"
              >
                <X className="w-3.5 h-3.5" />
                Show all operators
              </button>
            </div>
          )}
        </aside>

        <div className="space-y-5">
          {loading && (
            <div className="text-slate py-12 text-center">
              Loading vacancies…
            </div>
          )}

          {!loading && vacancies.length === 0 && (
            <div
              className="paper-card p-12 text-center text-slate"
              data-testid="vac-empty"
            >
              No vacancies match your filters. Try broadening your search.
            </div>
          )}

          {!loading && vacancies.length > 0 && (
            <div className="paper-card p-5 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div>
                <div className="text-[11px] uppercase tracking-[0.18em] font-semibold text-sky">
                  Jobs displayed
                </div>

                <div className="text-slate text-[13px] mt-1">
                  Showing{" "}
                  <span className="text-ice font-semibold">
                    {displayedTotal}
                  </span>{" "}
                  of{" "}
                  <span className="text-ice font-semibold">
                    {vacancies.length}
                  </span>{" "}
                  matching vacancies
                </div>
              </div>

              <div className="w-full md:w-[170px]">
                <Select value={listingCount} onValueChange={updateListingCount}>
                  <SelectTrigger
                    className="bg-nav border-line"
                    data-testid="vac-listing-count-select"
                  >
                    <SelectValue />
                  </SelectTrigger>

                  <SelectContent>
                    {LISTING_COUNTS.map((o) => (
                      <SelectItem key={o.v} value={o.v}>
                        {o.l}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}

          {featured.length > 0 && (
            <>
              <div className="text-[11px] uppercase tracking-[0.18em] font-semibold text-sky">
                Featured
              </div>

              <div className="grid md:grid-cols-2 gap-5">
                {featured.map((v) => (
                  <JobCard key={v.id} vacancy={v} />
                ))}
              </div>

              {standard.length > 0 && (
                <div className="text-[11px] uppercase tracking-[0.18em] font-semibold text-slate mt-8">
                  All roles
                </div>
              )}
            </>
          )}

          {!loading && displayedStandard.length > 0 && (
            <div className="grid md:grid-cols-2 gap-5">
              {displayedStandard.map((v) => (
                <JobCard key={v.id} vacancy={v} />
              ))}
            </div>
          )}

          {!loading && hiddenStandardCount > 0 && (
            <div className="paper-card p-5 text-center">
              <div className="text-slate text-sm">
                {hiddenStandardCount} more{" "}
                {hiddenStandardCount === 1 ? "vacancy is" : "vacancies are"}{" "}
                available.
              </div>

              <Button
                variant="outline"
                onClick={() => updateListingCount("all")}
                className="mt-4 border-line text-ice hover:bg-raise"
                data-testid="vac-show-all"
              >
                Show all vacancies
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}